// Best-effort extraction of a PDF's first-page aspect ratio without a parser.
// Scans the file for /MediaBox in plain text first, then attempts to deflate
// each stream block and scan inside (PDF 1.5+ object streams pack page dicts
// into compressed streams). Falls back to null when the file is encrypted,
// uses unsupported compression, or simply doesn't expose MediaBox anywhere
// we can read it — callers should default to a sensible aspect (e.g. letter).

const MEDIA_BOX_RE = /\/MediaBox\s*\[\s*([\d.-]+)\s+([\d.-]+)\s+([\d.-]+)\s+([\d.-]+)\s*\]/;

function extractMediaBox(s: string): { width: number; height: number } | null {
  const m = s.match(MEDIA_BOX_RE);
  if (!m) return null;
  const width = Number(m[3]) - Number(m[1]);
  const height = Number(m[4]) - Number(m[2]);
  if (!isFinite(width) || !isFinite(height) || width <= 0 || height <= 0) return null;
  return { width, height };
}

async function inflate(input: Uint8Array, format: 'deflate' | 'deflate-raw'): Promise<Uint8Array> {
  const ds = new DecompressionStream(format);
  const stream = new Blob([input as unknown as BlobPart]).stream().pipeThrough(ds);
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

export async function getPdfPageAspectRatio(buffer: ArrayBuffer): Promise<number | null> {
  const bytes = new Uint8Array(buffer);
  const text = new TextDecoder('latin1').decode(bytes);

  const plain = extractMediaBox(text);
  if (plain) return plain.width / plain.height;

  const re = /stream\r?\n([\s\S]*?)\r?\nendstream/g;
  const decoder = new TextDecoder('latin1');
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    const streamStart = match.index + match[0].indexOf('\n') + 1;
    const streamEnd = streamStart + match[1].length;
    const chunk = bytes.subarray(streamStart, streamEnd);
    for (const format of ['deflate', 'deflate-raw'] as const) {
      try {
        const decoded = await inflate(chunk, format);
        const hit = extractMediaBox(decoder.decode(decoded));
        if (hit) return hit.width / hit.height;
        break;
      } catch {
        // Try the next format; if both fail this stream isn't deflate-encoded.
      }
    }
  }
  return null;
}
